import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import fr from './locales/fr.json';

export const LANGUAGES = ['fr', 'en'];
const STORAGE_KEY = 'inventorium.lang';

// Only an explicit choice (the FR | EN switcher, or the account's language)
// is remembered; a language guessed from the browser is not written down, so
// it keeps following the browser until the user picks one.
const remember = (lng) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
  } catch (e) {
    // Private mode or blocked storage: the choice lasts for this page only.
  }
};

// While someone is logged in, AuthGate gives a function that saves their
// choice on the account (PUT /auth/me/locale): the same language follows them
// to another browser, and their e-mails are written in it.
let saveForAccount = null;
export const setAccountLanguageSaver = (save) => { saveForAccount = save; };

/** The UI language, always 'fr' or 'en'. */
export const currentLanguage = () => (LANGUAGES.includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en');

// A language picked with the switcher while logged out, in this page session:
// it wins over the account's at the next login (and is saved to it).
let loggedOutChoice = null;

export function chooseLanguage(lng) {
  remember(lng);
  if (saveForAccount) saveForAccount(lng);
  else loggedOutChoice = lng;
  return i18n.changeLanguage(lng);
}

/** The language picked while logged out, if any; reading it clears it. */
export const takeLoggedOutChoice = () => {
  const choice = loggedOutChoice;
  loggedOutChoice = null;
  return choice;
};

/**
 * The language to show when an account arrives (login, or a page opened with
 * a session), and whether to save it to the account: a choice just made while
 * logged out wins and is saved; else the account's own; else (never chosen)
 * the language on screen, saved once.
 */
export function languageOnArrival(locale, { loggedOutChoice: picked, current }) {
  if (LANGUAGES.includes(picked)) return { show: picked, save: picked !== locale };
  if (LANGUAGES.includes(locale)) return { show: locale, save: false };
  return { show: current, save: true };
}

/**
 * An invitation link carries ?lang=<the invitation's language>: its screen
 * opens in that language unless the visitor already chose one (localStorage
 * comes first in the detection order). Only on /invite/ paths.
 */
export const inviteLinkLanguage = (pathname, search) => {
  if (!/^\/invite\//.test(pathname || '')) return undefined;
  const lang = new URLSearchParams(search || '').get('lang');
  return LANGUAGES.includes(lang) ? lang : undefined;
};

const inviteLinkDetector = {
  name: 'inviteLink',
  lookup: () => (typeof window === 'undefined' ? undefined : inviteLinkLanguage(window.location.pathname, window.location.search)),
};

/** The account's language (from /auth/me) wins over this browser's: shown, and remembered for the login screen. */
export function followAccountLanguage(lng) {
  if (!LANGUAGES.includes(lng)) return Promise.resolve();
  remember(lng);
  return lng === i18n.resolvedLanguage ? Promise.resolve() : i18n.changeLanguage(lng);
}

i18n
  .use((() => { const detector = new LanguageDetector(); detector.addDetector(inviteLinkDetector); return detector; })())
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, fr: { translation: fr } },
    supportedLngs: LANGUAGES,
    nonExplicitSupportedLngs: true, // fr-BE, en-GB… use fr, en
    load: 'languageOnly',
    fallbackLng: 'en',
    detection: {
      // The language picked with the switcher (see chooseLanguage), if any;
      // then an invitation link's; otherwise the browser's. Detection itself
      // never writes the choice.
      order: ['localStorage', 'inviteLink', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: [],
    },
    interpolation: { escapeValue: false }, // React escapes already
    react: { useSuspense: false },
  });

const syncHtmlLang = () => {
  document.documentElement.lang = i18n.resolvedLanguage || i18n.language || 'en';
};
i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang();

export default i18n;
